/**
 * Chapter 6 Cue Choice Component - Dew Cup (Sharing & Connection)
 *
 * Trauma-informed sharing activity offering two approaches:
 * Option 1: "Dew Cup Share" — I need / You need / Let's try win-win (original exercise)
 * Option 2: "Story Swap" — perspective-taking approach where children finish
 *            "If I were them, I'd feel ___" before moving to solutions, building
 *            empathy first so collaborative sharing flows more naturally
 *
 * Based on the same dual-path architecture as BreathingCueChoice (Chapter 1).
 */

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { X, Users, BookOpen, Info, Coffee } from "lucide-react";

interface Chapter6CueChoiceProps {
  onComplete: () => void;
  isCompleted: boolean;
  trackButton?: (buttonId: string, metadata?: any) => void;
}

/** Shows one of the two activity modes step-by-step */
function ActivityMode({ mode, onBack, onComplete }: { mode: 'share' | 'swap'; onBack: () => void; onComplete: () => void }) {
  const [currentStep, setCurrentStep] = useState(0);

  const shareSteps = [
    "Let's find a win-win together!",
    "Chant: \"Dew Cup Share.\"",
    "Hold your hands like a cup in front of you.",
    "Say: \"I need [what you need].\"",
    "Say: \"You need [what they need].\" Then: \"Let's try [win-win idea]!\""
  ];

  const swapSteps = [
    "Let's walk in someone else's footsteps first.",
    "Think of someone you disagree with or want to share with.",
    "Finish this sentence: \"If I were them, I'd feel ___.\" ",
    "Now finish: \"The most important thing to them is ___.\" ",
    "From here, what win-win could work for both of you?"
  ];

  const steps = mode === 'share' ? shareSteps : swapSteps;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-lg font-semibold text-cyan-800">
          {mode === 'share' ? (
            <><Users className="w-5 h-5 text-cyan-500" /> Dew Cup Share</>
          ) : (
            <><BookOpen className="w-5 h-5 text-teal-500" /> Story Swap</>
          )}
        </div>
      </div>

      <div className="bg-cyan-50 rounded-lg p-6 min-h-[120px] flex items-center justify-center border-2 border-cyan-200">
        <AnimatePresence mode="wait">
          <motion.p
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="text-xl text-gray-800 text-center leading-relaxed"
          >
            {steps[currentStep]}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Animated visual */}
      <div className="flex justify-center">
        {mode === 'share' ? (
          <div className="flex gap-4">
            <motion.div className="w-10 h-10 rounded-full bg-cyan-400" animate={{ x: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
            <motion.div className="w-10 h-10 rounded-full bg-teal-400" animate={{ x: [0, -8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
          </div>
        ) : (
          <motion.div
            className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          />
        )}
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-2">
        {steps.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentStep(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentStep ? 'bg-cyan-600 w-6' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      <div className="flex justify-between items-center pt-2">
        <Button variant="ghost" onClick={onBack} className="text-gray-600 hover:text-gray-800">
          ← Back to choices
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrentStep(p => Math.max(0, p - 1))} disabled={currentStep === 0} className="text-sm">Previous</Button>
          <Button variant="outline" onClick={() => setCurrentStep(p => Math.min(steps.length - 1, p + 1))} disabled={currentStep === steps.length - 1} className="text-sm">Next</Button>
        </div>
      </div>

      {currentStep === steps.length - 1 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center pt-2">
          <Button onClick={onComplete} className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3 text-base font-medium">
            {mode === 'share' ? 'I shared a win-win!' : 'I swapped stories!'}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}

/** Parent/Educator info panel */
function EducatorInfo({ onClose }: { onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 space-y-3"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-blue-900">For Parents & Educators</h4>
        </div>
        <button onClick={onClose} className="text-blue-400 hover:text-blue-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="text-sm text-blue-800 space-y-2">
        <p><strong>Why two approaches to sharing?</strong></p>
        <p>Some children struggle with negotiation-first sharing because they are still learning to regulate their own needs. Jumping straight to a win-win can feel cognitively overwhelming when a child is emotionally activated.</p>
        <p><strong>"Story Swap"</strong> teaches perspective-taking as the foundation for sharing. Research in developmental psychology shows that children who can name another person's emotional experience are significantly more likely to engage in spontaneous, generous sharing—without needing rules or negotiation frameworks.</p>
        <p>Empathy first, solutions second: both paths build lasting cooperative skills.</p>
      </div>
    </motion.div>
  );
}

/** Main Chapter 6 Cue Choice Component */
export function Chapter6CueChoice({ onComplete, isCompleted, trackButton }: Chapter6CueChoiceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'share' | 'swap' | null>(null);
  const [showEducatorInfo, setShowEducatorInfo] = useState(false);

  const handleClose = () => {
    setSelectedMode(null);
    setShowEducatorInfo(false);
    setIsOpen(false);
  };

  const handleComplete = () => {
    onComplete();
    handleClose();
  };

  return (
    <>
      {/* Glowing trigger button */}
      <motion.button
        className="relative bg-cyan-100 p-8 rounded-full border-4 border-white/70 shadow-2xl backdrop-blur-sm w-24 h-24 flex items-center justify-center"
        onClick={() => {
          trackButton?.('dew_cup_cue_open', { cue_name: 'Dew Cup' });
          setIsOpen(true);
        }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          scale: [1, 1.25, 1],
          boxShadow: [
            "0 0 0 0 rgba(6, 182, 212, 0.7)",
            "0 0 0 30px rgba(6, 182, 212, 0)",
            "0 0 0 0 rgba(6, 182, 212, 0.7)",
          ],
        }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <Coffee className="w-12 h-12 text-cyan-600" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-200 relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <div className="w-10 h-10 rounded-full bg-cyan-50 text-cyan-600 border-2 border-current flex items-center justify-center">
                    <Coffee className="w-5 h-5" />
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={handleClose} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
                <div className="text-center px-16">
                  <h3 className="text-xl font-bold">
                    <span className="font-serif font-bold bg-gradient-to-r from-cyan-600 via-teal-500 to-cyan-600 bg-clip-text text-transparent">Dew Cup Share</span>
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  {showEducatorInfo && <EducatorInfo onClose={() => setShowEducatorInfo(false)} />}

                  {!selectedMode && !showEducatorInfo && (
                    <motion.div key="choice" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                      <p className="text-center text-gray-700 mb-2">Choose the path that feels right in this moment:</p>
                      <button onClick={() => setShowEducatorInfo(true)} className="flex items-center gap-1 mx-auto text-sm text-blue-600 hover:text-blue-800 mb-4">
                        <Info className="w-4 h-4" /> For Parents/Educators
                      </button>

                      {/* Option 1 */}
                      <motion.button
                        onClick={() => { trackButton?.('dew_cup_path_share', { cue_name: 'Dew Cup' }); setSelectedMode('share'); }}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-cyan-50 to-teal-50 border-2 border-cyan-300 rounded-xl text-left hover:border-cyan-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0">
                            <Users className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-cyan-900 mb-1">Dew Cup Share</h4>
                            <p className="text-sm text-cyan-700">Chant, say what you need, say what they need, and find a win-win together</p>
                          </div>
                        </div>
                      </motion.button>

                      {/* Option 2 */}
                      <motion.button
                        onClick={() => { trackButton?.('dew_cup_path_swap', { cue_name: 'Dew Cup' }); setSelectedMode('swap'); }}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-300 rounded-xl text-left hover:border-teal-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
                            <BookOpen className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-teal-900 mb-1">Story Swap</h4>
                            <p className="text-sm text-teal-700">Step into the other person's story first — understand their feelings before finding a solution</p>
                          </div>
                        </div>
                      </motion.button>

                      <p className="text-center text-sm text-gray-500 mt-4">Sharing gets easier when we understand each other first.</p>
                    </motion.div>
                  )}

                  {selectedMode && !showEducatorInfo && (
                    <ActivityMode key={selectedMode} mode={selectedMode} onBack={() => setSelectedMode(null)} onComplete={handleComplete} />
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
